/**
 * Sales Report Styles
 * Configuration and styles for sales report generation
 */

export const REPORT_STYLES = `
  @import url('https://fonts.googleapis.com/css2?family=Roboto:wght@300;400;500;700&display=swap');

  body { 
    font-family: 'Roboto', 'Arial', sans-serif; 
    margin: 0; 
    padding: 20px; 
    color: #333;
    line-height: 1.5;
    background-color: #f8f9fa;
  }
  
  .report-container { 
    max-width: 1000px; 
    margin: 0 auto; 
    background-color: white;
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
    border-radius: 8px;
    overflow: hidden;
    padding: 30px;
  }
  
  .company-header { 
    display: flex; 
    align-items: center; 
    margin-bottom: 30px;
    padding-bottom: 20px;
    border-bottom: 1px solid #eee;
  }
  
  .company-logo { 
    max-width: 220px; 
    max-height: 80px; 
    margin-right: 30px; 
    object-fit: contain;
  }
  
  .report-title { 
    color: #0066cc; 
    margin: 0 0 8px 0;
    font-size: 28px;
    font-weight: bold;
  }
  
  .report-date { 
    color: #777; 
    margin-bottom: 8px;
    font-size: 16px;
  }
  
  .company-name {
    font-size: 20px;
    margin-top: 5px;
    color: #333;
    font-weight: 500;
  }
  
  .section {
    margin: 30px 0;
    page-break-inside: avoid;
  }
  
  .section-title {
    font-size: 22px;
    color: #0066cc;
    margin-bottom: 15px;
    padding-bottom: 8px;
    border-bottom: 2px solid #eee;
    font-weight: 500;
  }
  
  .analysis-text { 
    line-height: 1.7; 
    white-space: pre-line; 
    background-color: #f9f9f9; 
    padding: 20px; 
    border-radius: 8px;
    font-size: 15px;
    margin-bottom: 15px;
    border-left: 4px solid #0066cc;
  }
  
  .charts-container { 
    display: flex; 
    flex-wrap: wrap; 
    justify-content: space-around; 
    margin: 20px 0; 
    gap: 20px;
  }
  
  .chart-wrapper { 
    flex: 1 1 300px;
    min-width: 45%; 
    margin: 10px 0;
    background-color: white;
    border-radius: 8px;
    padding: 15px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
    position: relative;
  }
  
  .chart-title { 
    font-weight: bold; 
    text-align: center; 
    margin-bottom: 15px;
    color: #444;
    font-size: 16px;
    padding-bottom: 8px;
    border-bottom: 1px solid #eee;
  }
  
  .metrics-container {
    background-color: #f9f9f9;
    border-radius: 8px;
    padding: 20px;
    margin-top: 20px;
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
    gap: 20px;
  }
  
  .metric-item {
    padding: 15px;
    background-color: white;
    border-radius: 8px;
    box-shadow: 0 2px 4px rgba(0,0,0,0.05);
    display: flex;
    flex-direction: column;
  }
  
  .metric-label {
    font-weight: bold;
    color: #0066cc;
    margin-bottom: 5px;
    font-size: 14px;
  }
  
  .metric-value {
    font-size: 16px;
    color: #333;
  }
  
  .footer { 
    margin-top: 40px; 
    text-align: center; 
    color: #777; 
    font-size: 12px;
    padding-top: 15px;
    border-top: 1px solid #eee;
  }
  
  /* Responsive adjustments */
  @media (max-width: 768px) {
    .company-header {
      flex-direction: column;
      align-items: flex-start;
    }
    
    .company-logo {
      margin-bottom: 15px;
      margin-right: 0;
    }
    
    .metrics-container {
      grid-template-columns: 1fr;
    }
  }
  
  /* Print specific styles */
  @media print {
    body {
      background-color: white;
      padding: 0;
      font-size: 12pt;
    }
    
    .report-container {
      box-shadow: none;
      padding: 0;
      max-width: 100%;
    }
    
    .chart-wrapper { 
      page-break-inside: avoid;
      box-shadow: none;
      break-inside: avoid;
      min-width: 45%;
    }
    
    .section {
      page-break-inside: avoid;
      break-inside: avoid;
    }
    
    .metrics-container {
      page-break-inside: avoid;
      box-shadow: none;
      break-inside: avoid;
    }
    
    .metric-item {
      box-shadow: none;
    }
    
    /* Force background colors to print */
    * {
      -webkit-print-color-adjust: exact !important;
      print-color-adjust: exact !important;
    }
  }
`;

export const chartColorSchemes = {
  default: {
    backgroundColor: [
      'rgba(54, 162, 235, 0.7)',
      'rgba(255, 99, 132, 0.7)',
      'rgba(75, 192, 192, 0.7)',
      'rgba(255, 206, 86, 0.7)',
      'rgba(153, 102, 255, 0.7)',
      'rgba(255, 159, 64, 0.7)'
    ],
    borderColor: [
      'rgba(54, 162, 235, 1)',
      'rgba(255, 99, 132, 1)',
      'rgba(75, 192, 192, 1)',
      'rgba(255, 206, 86, 1)',
      'rgba(153, 102, 255, 1)',
      'rgba(255, 159, 64, 1)'
    ]
  },
  brands: {
    backgroundColor: [
      'rgba(53, 97, 171, 0.7)', // VW Blue
      'rgba(208, 9, 38, 0.7)',  // Audi Red
      'rgba(0, 153, 51, 0.7)',  // Skoda Green
      'rgba(0, 60, 113, 0.7)',  // Ford Blue
      'rgba(0, 0, 0, 0.7)',     // Porsche Black
      'rgba(226, 6, 19, 0.7)'   // Citroen Red
    ],
    borderColor: [
      'rgba(53, 97, 171, 1)',
      'rgba(208, 9, 38, 1)',
      'rgba(0, 153, 51, 1)',
      'rgba(0, 60, 113, 1)',
      'rgba(0, 0, 0, 1)',
      'rgba(226, 6, 19, 1)'
    ]
  },
  bodyTypes: {
    backgroundColor: [
      'rgba(75, 192, 192, 0.7)',  // SUV - Teal
      'rgba(54, 162, 235, 0.7)',  // Berlina - Blue
      'rgba(255, 206, 86, 0.7)',  // City Car - Yellow
      'rgba(153, 102, 255, 0.7)', // Wagon - Purple
      'rgba(255, 99, 132, 0.7)'   // Coupé - Pink
    ],
    borderColor: [
      'rgba(75, 192, 192, 1)',
      'rgba(54, 162, 235, 1)',
      'rgba(255, 206, 86, 1)',
      'rgba(153, 102, 255, 1)',
      'rgba(255, 99, 132, 1)'
    ]
  },
  salesStatus: {
    backgroundColor: [
      'rgba(54, 162, 235, 0.7)', // Vendute - Blue
      'rgba(255, 159, 64, 0.7)'  // Disponibili - Orange
    ],
    borderColor: [
      'rgba(54, 162, 235, 1)',
      'rgba(255, 159, 64, 1)'
    ]
  }
};

// Utility function to ensure we have at least some default data
export const ensureChartData = (chartData, key, defaultTitle) => {
  if (!chartData || !chartData[key] || !chartData[key].data || !chartData[key].labels) {
    return {
      title: defaultTitle,
      labels: ['No Data'],
      data: [1]
    };
  }
  return chartData[key];
};

export const getChartColors = (chartKey) => {
  if (chartKey.includes('distribution') || chartKey.includes('brand')) {
    return chartColorSchemes.brands;
  } else if (chartKey.includes('body') || chartKey.includes('style')) {
    return chartColorSchemes.bodyTypes;
  } else if (chartKey.includes('status')) {
    return chartColorSchemes.salesStatus;
  }
  return chartColorSchemes.default;
};

export default {
  REPORT_STYLES,
  chartColorSchemes,
  getChartColors,
  ensureChartData
}; 